/**
 * 디바이스 언어로 앱 제목(document.title)을 현지화하는 재사용 유틸.
 *
 * 네이티브 앱 이름(Android strings.xml / iOS Info.plist)은 빌드 타임 리소스 한정자(values-<lang>/)로
 * OS가 디바이스 언어에 맞춰 고르지만, **웹/PWA의 브라우저 탭 제목(document.title)** 은 런타임에
 * 자바스크립트로만 바꿀 수 있다. 이 패키지는 그 런타임 경로를 담당한다:
 *   navigator.language → 지원 언어 코드 정규화 → entries에서 제목 선택 → document.title + <html lang> 반영.
 *
 * 입력은 `AppTitleEntry[]` — 앱이 데이터(예: appTitle.tsv → appTitle.json)에서 그대로 넘기는 행 배열.
 * fallback은 별도 인자가 아니라 `default: true` 행으로 데이터가 표현한다(없으면 첫 행). 이로써
 * "어느 언어가 기본인가"를 코드가 아닌 데이터가 소유한다(no-hardcode).
 *
 * DOM(`document`/`navigator`)을 직접 import하지 않고 최소 Port(`TitleTarget`/`LanguageSource`)로 받아
 * 순수 함수로 테스트 가능하게 한다 (DIP).
 */

/** 앱 제목 데이터 한 행 — appTitle.tsv 한 줄과 1:1 (langCode/title/shortTitle/default). */
export interface AppTitleEntry {
  /** 타겟 언어 코드 (BCP-47 prefix 매칭 대상, 예: "ko"·"en"). */
  readonly langCode: string;
  /** 현지화된 앱 제목 — 웹 탭 제목 / Android app_name. */
  readonly title: string;
  /** iOS 숏네임(CFBundleName) 등 짧은 표기. 비면 title로 폴백. */
  readonly shortTitle?: string;
  /** true면 이 행이 fallback(미지원/미감지 디바이스 언어가 떨어지는 기본). */
  readonly default?: boolean;
}

/** DOM 의존 격리 Port — document의 title/lang만 추상화 (테스트는 mock으로 주입). */
export interface TitleTarget {
  title: string;
  readonly documentElement: { lang: string };
}

/** navigator 최소 형태 — language 태그만 사용. */
export interface LanguageSource {
  readonly language?: string;
}

/** 해석 결과 — locale(선택된 언어코드) + 제목 + 숏제목. */
export interface ResolvedAppTitle {
  readonly locale: string;
  readonly title: string;
  readonly shortTitle: string;
}

/**
 * raw 언어 태그(navigator.language, 예: "en-US")를 지원 언어 코드로 정규화.
 * 소문자화 후 정확 일치 또는 `code-` 접두 일치 — "en-US"→"en", "ko"→"ko". 미매칭/undefined면 fallback.
 */
export function pickLocale<L extends string>(
  raw: string | undefined,
  supported: readonly L[],
  fallback: L,
): L {
  if (raw) {
    const lang = raw.toLowerCase();
    for (const code of supported) {
      if (lang === code || lang.startsWith(code + "-")) return code;
    }
  }
  return fallback;
}

/** entries에서 fallback 언어코드 선택 — default:true 행, 없으면 첫 행. */
function fallbackLangCode(entries: readonly AppTitleEntry[]): string {
  return entries.find((e) => e.default)?.langCode ?? entries[0]?.langCode ?? "";
}

/**
 * entries + raw 언어 태그 → { locale, title, shortTitle }.
 * locale 행이 없으면 fallback 행으로 보정. shortTitle이 비면 title로 폴백.
 */
export function resolveAppTitle(
  entries: readonly AppTitleEntry[],
  rawLanguage: string | undefined,
): ResolvedAppTitle {
  const fallback = fallbackLangCode(entries);
  const supported = entries.map((e) => e.langCode);
  const locale = pickLocale(rawLanguage, supported, fallback);
  const entry =
    entries.find((e) => e.langCode === locale) ??
    entries.find((e) => e.langCode === fallback);
  const title = entry?.title ?? "";
  return { locale, title, shortTitle: entry?.shortTitle || title };
}

/** 해석 결과를 DOM(또는 mock)에 반영 — document.title + <html lang>. 빈 제목은 덮어쓰지 않는다(웹은 shortTitle 미사용). */
export function applyAppTitle(target: TitleTarget, resolved: ResolvedAppTitle): void {
  if (resolved.title) target.title = resolved.title;
  target.documentElement.lang = resolved.locale;
}

/**
 * 편의 통합 — navigator.language로 해석 후 target에 적용하고 결과 반환.
 * 부팅 매우 초기(Phaser 시작 전)에 1회 호출하면 디바이스 언어로 제목이 즉시 반영된다.
 */
export function installAppTitle(
  entries: readonly AppTitleEntry[],
  target: TitleTarget,
  source: LanguageSource,
): ResolvedAppTitle {
  const resolved = resolveAppTitle(entries, source.language);
  applyAppTitle(target, resolved);
  return resolved;
}
